import mapKnownValues from "../utils/mapKnownValues";
import {
  STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS,
  STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT,
  STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT,
  STEP_OLD_ACCOUNT_SPONSOR_NAME,
  STEP_OLD_ACCOUNT_UPLOAD_STATEMENT,
} from "./transferDetails";
import { PartialTransfer } from "./transferIntent";

export const UNKNOWN = "UNKNOWN";
export const TRUE = "TRUE";
export const FALSE = "FALSE";
export type SkippableBoolean = typeof UNKNOWN | typeof TRUE | typeof FALSE;

export type SourceAccountResponse = {
  planSponsor: string | null;
  terminatedYear: string | null;
  startYear: string | null;
  statementSkipped: SkippableBoolean;
  providerName?: string | null;
  transferId: number;
  sourceAccountId: number;
  sourceStatementFileNames: string[];
  providerSkipped: SkippableBoolean;
  salaryRange: string | null;
  aggregatorLink: null | string;
  aggregatorPublicToken: null | string;
  aggregatorSkipped: SkippableBoolean;
  partialTransfer?: PartialTransfer;
};

// TODO: revisit this logic
export const transformSkippableToBoolean = (input: SkippableBoolean | boolean | null): boolean => {
  if (!input) return false;
  if (input === UNKNOWN) return false;
  if (typeof input === "boolean") return input;
  if (input === "TRUE") return true;
  return false;
};

export default class SourceAccount {
  transferId = -1;
  sourceAccountId = -1;
  planSponsor = "";
  planSponsorId = "";
  terminatedYear = "";
  startYear = "";
  providerName = "";
  planName: string | null = "";
  planNumber: string | null = "";
  providerSkipped: SkippableBoolean = "UNKNOWN";
  statementSkipped: typeof UNKNOWN | null | "TRUE" | "FALSE" = UNKNOWN;
  sourceStatementFileNames: string[] = [];
  transferInstructionId = -1;
  partialTransfer: PartialTransfer | null = null;

  constructor(params: Partial<SourceAccount> = {}) {
    // TODO: add transform skippable boolean to undefined
    mapKnownValues(this, params);
  }

  toJSON() {
    const data = {
      ...this,
      planSponsor: this.planSponsor || null,
      terminatedYear: this.terminatedYear || null,
      startYear: this.startYear || null,
    } as Record<keyof SourceAccount, any>;

    return data;
  }

  addFileNameToStatements(fileName: string) {
    if (this.sourceStatementFileNames.includes(fileName)) return;
    this.sourceStatementFileNames.push(fileName);
  }

  removeFileNameFromStatements(fileName: string) {
    const indexOfFileNameFound = this.sourceStatementFileNames.indexOf(fileName);
    if (indexOfFileNameFound === -1) return;
    this.sourceStatementFileNames.splice(indexOfFileNameFound, 1);
  }

  get isCompleted() {
    let completed = false;
    if (this.planSponsor && this.statementSkipped === "FALSE" && this.sourceStatementFileNames.length > 0) {
      completed = true;
    }
    if (
      this.planSponsor &&
      this.statementSkipped === "TRUE" &&
      (this.providerName || this.providerSkipped === "TRUE")
    ) {
      completed = true;
    }
    return completed;
  }

  get currentStep() {
    if (!this.planSponsor) {
      return STEP_OLD_ACCOUNT_SPONSOR_NAME;
    }
    if (
      this.planSponsor &&
      (this.statementSkipped === undefined || this.statementSkipped === "UNKNOWN" || this.statementSkipped === null)
    ) {
      return STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT;
    }
    if (this.planSponsor && this.statementSkipped === "TRUE" && (!this.startYear || !this.terminatedYear)) {
      return STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS;
    }
    if (this.planSponsor && this.statementSkipped === "FALSE" && this.sourceStatementFileNames.length === 0) {
      return STEP_OLD_ACCOUNT_UPLOAD_STATEMENT;
    }
    return STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT;
  }

  static toJSON(sourceAccount: SourceAccount) {
    const jsonRequest = {
      ...sourceAccount,
      transferId: sourceAccount.transferId,
      sourceAccountId: sourceAccount.sourceAccountId,
      planSponsor: sourceAccount.planSponsor || undefined,
      terminatedYear: sourceAccount.terminatedYear || undefined,
      startYear: sourceAccount.startYear || undefined,
      providerName: sourceAccount.providerName || undefined,
      providerSkipped: sourceAccount.providerName ? "FALSE" : "TRUE",
      sourceStatementFileNames: sourceAccount.sourceStatementFileNames,
      statementSkipped:
        sourceAccount.sourceStatementFileNames.length && sourceAccount.sourceStatementFileNames.length > 0
          ? "FALSE"
          : "TRUE",
      planNumber: sourceAccount.planNumber ? parseInt(sourceAccount.planNumber) : 0,
    };
    return jsonRequest;
  }

  static fromApi(response: SourceAccountResponse | undefined) {
    if (!response) return new SourceAccount();
    const newResponse = {
      ...response,
      transferId: response.transferId,
      sourceAccountId: response.sourceAccountId,
      planSponsor: response.planSponsor || undefined,
      terminatedYear: response.terminatedYear || undefined,
      startYear: response.startYear || undefined,
      providerName: response.providerName || undefined,
      sourceStatementFileNames: response.sourceStatementFileNames,
      statementSkipped: response.statementSkipped,
    };
    return new SourceAccount(newResponse);
  }
}
